import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { vacancyService } from '../../services/vacancyService';
import { authService } from '../../services/authService';
import EmployeeBottomNavigation from '../../components/EmployeeBottomNavigation';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import EmployeeNav from '../../components/EmployeeNav';
import Loader from '../../components/Loader';
import { useTranslation } from '../../hooks/useTranslation';

const Applications = () => {
    const navigate = useNavigate();
    const user = authService.getCurrentUser();
    const { t, locale } = useTranslation();
    const [applications, setApplications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState('all');

    useEffect(() => {
        loadApplications();
    }, []);

    const loadApplications = async () => {
        try {
            setLoading(true);
            const response = await vacancyService.getApplications();
            if (response.success) {
                setApplications(response.data || []);
            }
        } catch (error) {
            toast.error(t('employee.applications.loadFailed'));
        } finally {
            setLoading(false);
        }
    };

    const handleWithdraw = async (vacancyId) => {
        const dir = locale === 'ar' ? 'rtl' : 'ltr';
        const result = await Swal.fire({
            title: t('employee.applications.withdrawConfirmTitle'),
            text: t('employee.applications.withdrawConfirmText'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#608fd3',
            cancelButtonColor: '#6b7280',
            confirmButtonText: t('employee.applications.withdrawConfirmButton'),
            cancelButtonText: t('employee.applications.withdrawCancelButton'),
            reverseButtons: locale === 'ar',
            customClass: {
                popup: 'swal2-rtl',
                title: 'swal2-title-rtl',
                htmlContainer: 'swal2-html-container-rtl',
                confirmButton: 'swal2-confirm-rtl',
                cancelButton: 'swal2-cancel-rtl'
            }
        });

        if (result.isConfirmed) {
            try {
                const response = await vacancyService.withdrawApplication(vacancyId);
                if (response.success) {
                    Swal.fire({
                        title: t('employee.applications.withdrawSuccessTitle'),
                        text: t('employee.applications.withdrawSuccessText'),
                        icon: 'success',
                        confirmButtonColor: '#608fd3',
                        confirmButtonText: t('employee.applications.ok'),
                        customClass: {
                            popup: 'swal2-rtl',
                            title: 'swal2-title-rtl',
                            htmlContainer: 'swal2-html-container-rtl',
                            confirmButton: 'swal2-confirm-rtl'
                        }
                    });
                    loadApplications();
                }
            } catch (error) {
                Swal.fire({
                    title: t('employee.applications.withdrawFailedTitle'),
                    text: t('employee.applications.withdrawFailedText'),
                    icon: 'error',
                    confirmButtonColor: '#608fd3',
                    confirmButtonText: t('employee.applications.ok'),
                    customClass: {
                        popup: 'swal2-rtl',
                        title: 'swal2-title-rtl',
                        htmlContainer: 'swal2-html-container-rtl',
                        confirmButton: 'swal2-confirm-rtl'
                    }
                });
            }
        }
    };

    const handleJobClick = (jobId) => {
        navigate(`/employee/vacancies/${jobId}`);
    };

    const formatSalary = (salary, currency) => {
        if (!salary) return t('employee.home.salaryNotSpecified');
        if (salary >= 1000) {
            return `${Math.round(salary / 1000)}k ${currency} ${t('employee.home.perMonth')}`;
        }
        return `${salary} ${currency} ${t('employee.home.perMonth')}`;
    };

    const formatWorkType = (type) => {
        const types = {
            'full_time': t('employee.home.workType.fullTime'),
            'part_time': t('employee.home.workType.partTime'),
            'contract': t('employee.home.workType.contract'),
            'freelance': t('employee.home.workType.freelance')
        };
        return types[type] || type;
    };

    const formatPresenceType = (type) => {
        const types = {
            'remote': t('employee.home.presenceType.remote'),
            'hybrid': t('employee.home.presenceType.hybrid'),
            'onsite': t('employee.home.presenceType.onsite')
        };
        return types[type] || type;
    };

    const formatLocation = (location) => {
        if (!location) return 'القاهرة';
        if (typeof location === 'string') return location;
        if (typeof location === 'object' && (location.lat || location.long)) {
            return 'القاهرة';
        }
        return 'القاهرة';
    };

    const formatTimeAgo = (dateString) => {
        if (!dateString) return t('employee.home.salaryNotSpecified');
        const date = new Date(dateString);
        const now = new Date();
        const diffTime = Math.abs(now - date);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays === 0) return t('employee.applications.timeAgo.today');
        if (diffDays === 1) return t('employee.applications.timeAgo.yesterday');
        if (diffDays < 7) return t('employee.applications.timeAgo.daysAgo').replace('{days}', diffDays);
        if (diffDays < 30) return t('employee.applications.timeAgo.weeksAgo').replace('{weeks}', Math.floor(diffDays / 7));
        if (diffDays < 365) return t('employee.applications.timeAgo.monthsAgo').replace('{months}', Math.floor(diffDays / 30));
        return t('employee.applications.timeAgo.yearsAgo').replace('{years}', Math.floor(diffDays / 365));
    };

    const getStatusLabel = (status) => {
        const labels = {
            'pending': t('employee.applications.status.pending'),
            'viewed': t('employee.applications.status.viewed'),
            'accepted': t('employee.applications.status.accepted'),
            'rejected': t('employee.applications.status.rejected')
        };
        return labels[status] || status;
    };

    const getStatusBadgeClass = (status) => {
        switch (status) {
            case 'accepted':
                return 'status-badge-accepted';
            case 'rejected':
                return 'status-badge-rejected';
            case 'viewed':
                return 'status-badge-viewed';
            default:
                return 'status-badge-pending';
        }
    };

    // Filter applications by status
    const filteredApplications = statusFilter === 'all'
        ? applications
        : applications.filter(app => app.status === statusFilter);

    return (
        <div className="employee-home-page" dir={locale === 'ar' ? 'rtl' : 'ltr'}>
            {/* Header with Notification and Profile */}
            <EmployeeNav user={user} />


            {/* Status Filter Tabs */}
            <div className="filter-tabs-container px-3 mb-3">
                <div className="filter-tabs d-flex gap-2">
                    <button
                        className={`filter-tab ${statusFilter === 'all' ? 'active' : ''}`}
                        onClick={() => setStatusFilter('all')}
                    >
                        {t('employee.applications.status.all')}
                    </button>
                    <button
                        className={`filter-tab ${statusFilter === 'pending' ? 'active' : ''}`}
                        onClick={() => setStatusFilter('pending')}
                    >
                        {t('employee.applications.status.pending')}
                    </button>
                    <button
                        className={`filter-tab ${statusFilter === 'viewed' ? 'active' : ''}`}
                        onClick={() => setStatusFilter('viewed')}
                    >
                        {t('employee.applications.status.viewed')}
                    </button>
                    <button
                        className={`filter-tab ${statusFilter === 'accepted' ? 'active' : ''}`}
                        onClick={() => setStatusFilter('accepted')}
                    >
                        {t('employee.applications.status.accepted')}
                    </button>
                    <button
                        className={`filter-tab ${statusFilter === 'rejected' ? 'active' : ''}`}
                        onClick={() => setStatusFilter('rejected')}
                    >
                        {t('employee.applications.status.rejected')}
                    </button>
                </div>
            </div>

            {/* Applications List */}
            <div className="all-jobs-section px-3 mb-5">
                <h3 className="section-title mb-3">{t('employee.applications.title')}</h3>
                <div className="jobs-list">
                    {loading ? (
                        <Loader size="medium" />
                    ) : filteredApplications.length > 0 ? (
                        filteredApplications.map((application) => {
                            const vacancy = application.vacancy;
                            if (!vacancy) return null;

                            return (
                                <div key={application.id} className="job-card mb-3">
                                    <div className="d-flex align-items-start gap-3">
                                        <div className="company-logo-card">
                                            {vacancy.company?.logo ? (
                                                <img
                                                    src={vacancy.company?.logo}
                                                    alt="Company Logo"
                                                    className="logo-img"
                                                />
                                            ) : (
                                                <div className="company-logo-text text-dark align-content-center">
                                                    <b>
                                                        {vacancy.company?.name?.charAt(0).toUpperCase() || 'C'}
                                                    </b>
                                                </div>
                                            )}
                                        </div>
                                        <div className="job-content flex-grow-1">
                                            <div className="d-flex justify-content-between align-items-start mb-2">
                                                <div className="flex-grow-1">
                                                    <div className="job-title-card mb-1">
                                                        {vacancy.job_title?.name || t('employee.home.jobTitle')}
                                                    </div>
                                                    <div className="job-company-location mb-2">
                                                        {vacancy.company?.name || t('employee.home.companyName')} - {formatLocation(vacancy.location)} - مصر
                                                    </div>
                                                    <div className="job-salary mb-2">
                                                        {formatSalary(vacancy.salary, vacancy.currency)}
                                                    </div>
                                                    <div className="job-tags d-flex gap-2 mb-2">
                                                        <span className="job-tag">{formatPresenceType(vacancy.presence_type)}</span>
                                                        <span className="job-tag">{formatWorkType(vacancy.type_of_work)}</span>
                                                        <span className={`status-badge ${getStatusBadgeClass(application.status)}`}>
                                                            {getStatusLabel(application.status)}
                                                        </span>
                                                    </div>
                                                    <div className="application-date mb-2">
                                                        <small className="text-muted">
                                                            {t('employee.applications.appliedAt')} {formatTimeAgo(application.applied_at)}
                                                        </small>
                                                    </div>
                                                </div>
                                                <button className="job-menu-btn">
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <circle cx="12" cy="12" r="1" />
                                                        <circle cx="12" cy="5" r="1" />
                                                        <circle cx="12" cy="19" r="1" />
                                                    </svg>
                                                </button>
                                            </div>
                                            <div className="d-flex gap-2">
                                                <button
                                                    className="btn-view-vacancy"
                                                    onClick={() => handleJobClick(vacancy.id)}
                                                >
                                                    {t('employee.applications.viewVacancy')}
                                                </button>
                                                {application.status === 'pending' && (
                                                    <button
                                                        className="btn-withdraw"
                                                        onClick={() => handleWithdraw(vacancy.id)}
                                                    >
                                                        {t('employee.applications.withdrawApplication')}
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    ) : (
                        <div className="text-center py-4">
                            <p>{t('employee.applications.noApplications')}</p>
                            <button
                                className="btn-apply mt-3"
                                onClick={() => navigate('/employee/home')}
                            >
                                {t('employee.applications.browseJobs')}
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Bottom Navigation Bar */}
            <EmployeeBottomNavigation />
        </div>
    );
};

export default Applications;
